/**
 * The master price list, and the arithmetic every quote shares.
 *
 * Proposals, bids and invoices each used to work out their own numbers: a
 * payment split typed by hand, a discount remembered or not, a rush fee argued
 * on the phone. The policies are written down once, in `catalog_policies`, and
 * this is the one place that applies them.
 *
 * Nothing here guesses a number from prose. Each policy carries a `rule` the
 * engine reads; a policy that is missing simply does not apply, rather than
 * falling back to a figure somebody once wrote in a PDF.
 *
 * PMI vocabulary, because the list is laid out that way:
 *   program   a group of services           (Brand Strategy …)
 *   service   cost + schedule baseline      (starts at, range, weeks)
 *   work pkg  one deliverable in the WBS    (the scope, in full)
 *   baseline  the list as it stood when a document was quoted
 */

const n = (v) => {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

export const money = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n(v))

/** A recurring service is billed monthly; everything else is one-time work. */
export const isRetainer = (service) => service?.price_type === 'recurring'

/** Core services on the list, as opposed to add-ons quoted beside them. */
export const isCoreService = (service) => service?.offering === 'service'

const policy = (policies, id) => (policies || []).find(p => p.id === id)?.rule || null

/* ── Reading the list ────────────────────────────────────────────────────── */

/**
 * Programs with their services in order, archived and inactive left out.
 *
 * Add-ons are grouped under their own program rather than mixed into the core
 * list, so the ten services stay the thing a client is shown first.
 */
export function byProgram(programs = [], services = [], workPackages = []) {
  const wps = {}
  for (const w of workPackages || []) (wps[w.service_id] ||= []).push(w)
  for (const k of Object.keys(wps)) wps[k].sort((a, b) => n(a.sort_order) - n(b.sort_order))

  return (programs || [])
    .filter(p => p.is_active !== false)
    .sort((a, b) => n(a.sort_order) - n(b.sort_order))
    .map(p => ({
      ...p,
      services: (services || [])
        .filter(s => s.program_id === p.id && s.is_active !== false && s.offering !== 'archived')
        .sort((a, b) => n(a.sort_order) - n(b.sort_order) || String(a.service).localeCompare(String(b.service)))
        .map(s => ({ ...s, work_packages: wps[s.id] || [] })),
    }))
    .filter(p => p.services.length > 0)
}

/** "2–3 weeks", or "Monthly, 6-month minimum" — the schedule baseline in words. */
export function describeSchedule(service) {
  if (!service) return ''
  if (isRetainer(service)) {
    return service.min_term_months ? `Monthly, ${service.min_term_months}-month minimum` : 'Monthly'
  }
  const lo = n(service.weeks_min)
  const hi = n(service.weeks_max)
  if (!lo && !hi) return service.turnaround || ''
  if (lo && hi && lo !== hi) return `${lo}–${hi} weeks`
  const w = lo || hi
  return `${w} week${w === 1 ? '' : 's'}`
}

/** "$8,500 – $15,000", or "$4,500 – $8,000/mo". */
export function describePrice(service) {
  if (!service) return ''
  const unit = isRetainer(service) ? '/mo' : ''
  const lo = n(service.price_amount)
  const hi = n(service.price_max)
  if (hi && hi > lo) return `${money(lo)} – ${money(hi)}${unit}`
  return `${money(lo)}${unit}`
}

/* ── Putting a service on a document ─────────────────────────────────────── */

/**
 * A service as a line on a quote, baselined.
 *
 * The price and the deliverables are copied, together with the list version
 * they came from, so editing the master list later cannot reach back into a
 * document that has already been sent. That is what a baseline is for.
 */
export function lineFromService(service, { version = null, amount = null } = {}) {
  const retainer = isRetainer(service)
  return {
    service_id: service.id,
    program_id: service.program_id || null,
    label: service.service,
    sub: service.summary || '',
    amount: amount === null ? n(service.price_amount) : n(amount),
    kind: retainer ? 'retainer' : isCoreService(service) ? 'service' : 'addon',
    months: retainer ? n(service.min_term_months) || null : null,
    floor: n(service.price_amount),
    ceiling: n(service.price_max) || null,
    requires: service.standalone === false ? service.requires_service_id || null : null,
    work_packages: (service.work_packages || []).map(w => (typeof w === 'string' ? w : w.name)),
    schedule: describeSchedule(service),
    price_list_version: version,
  }
}

/* ── The arithmetic ──────────────────────────────────────────────────────── */

/**
 * The payment schedule for one-time work.
 *
 * "Projects over $25K" is read as strictly over: a $25,000 project is not a
 * large project. The last instalment absorbs the rounding, so the parts always
 * add back up to the total rather than a dollar short of it.
 */
export function paymentSchedule(total, policies = []) {
  const rule = policy(policies, 'payment-terms')
  if (!rule || n(total) <= 0) return []
  const large = rule.large_project_over && n(total) > n(rule.large_project_over)
  const splits = (large ? rule.large_project : rule.project) || []
  if (!splits.length) return []

  const names = splits.length === 2
    ? ['To start', 'At final delivery']
    : splits.map((_, i) => (i === 0 ? 'To start' : `Phase ${i + 1}`))

  let assigned = 0
  return splits.map((pct, i) => {
    const last = i === splits.length - 1
    const amount = last ? n(total) - assigned : Math.round((n(total) * n(pct)) / 100)
    assigned += amount
    return { label: names[i], pct: n(pct), amount }
  })
}

/**
 * Everything a quote needs worked out, from its lines and the policies.
 *
 * Order matters and is fixed: the combined credit comes off the core services
 * first, then a rush fee is charged on the one-time work that remains. Stating
 * the order is the difference between one answer and two.
 *
 * `compressionPct` is how much shorter the agreed timeline is than the
 * schedule baseline — 40 means delivered in 60% of the usual time.
 */
export function quote(lines = [], { policies = [], compressionPct = 0 } = {}) {
  const services = lines.filter(l => l.kind === 'service')
  const addons = lines.filter(l => l.kind === 'addon')
  const retainers = lines.filter(l => l.kind === 'retainer')
  const sum = (ls) => ls.reduce((t, l) => t + n(l.amount), 0)

  const servicesSubtotal = sum(services)
  const addonsSubtotal = sum(addons)

  const credit = policy(policies, 'combined-credit')
  const creditApplies = credit && services.length >= n(credit.min_project_services)
  const combinedCredit = creditApplies ? Math.round((servicesSubtotal * n(credit.credit_pct)) / 100) : 0

  const oneTimeBeforeRush = servicesSubtotal - combinedCredit + addonsSubtotal

  const rush = policy(policies, 'rush-work')
  const rushApplies = rush && n(compressionPct) > n(rush.compression_over_pct)
  const rushFee = rushApplies ? Math.round((oneTimeBeforeRush * n(rush.fee_pct)) / 100) : 0

  const oneTimeTotal = oneTimeBeforeRush + rushFee
  const monthly = sum(retainers)
  const commitment = retainers.reduce((t, l) => t + n(l.amount) * (n(l.months) || 1), 0)

  return {
    servicesSubtotal,
    addonsSubtotal,
    combinedCredit,
    creditPct: creditApplies ? n(credit.credit_pct) : 0,
    rushFee,
    rushPct: rushApplies ? n(rush.fee_pct) : 0,
    oneTimeTotal,
    monthly,
    commitment,
    schedule: paymentSchedule(oneTimeTotal, policies),
    warnings: warningsFor(lines, policies),
  }
}

/**
 * What a person should look at before this quote goes out.
 *
 * Warnings, not refusals. Every one of these has a legitimate exception — a
 * client whose existing positioning passes review, a strategic price under the
 * floor — so the engine says so and leaves the decision where it belongs.
 */
export function warningsFor(lines = [], policies = []) {
  const out = []
  const ids = new Set(lines.map(l => l.service_id))
  const standalone = policy(policies, 'standalone-rule')

  for (const l of lines) {
    if (l.requires && !ids.has(l.requires)) {
      out.push({
        kind: 'predecessor',
        service_id: l.service_id,
        message: `${l.label} needs Positioning & Messaging first${standalone?.waiver ? `, or ${standalone.waiver}` : ''}.`,
      })
    }
    if (l.floor && n(l.amount) < l.floor) {
      out.push({
        kind: 'below-floor',
        service_id: l.service_id,
        message: `${l.label} is quoted at ${money(l.amount)}, under its ${money(l.floor)} starting price.`,
      })
    }
    if (l.ceiling && n(l.amount) > l.ceiling) {
      out.push({
        kind: 'above-range',
        service_id: l.service_id,
        message: `${l.label} is quoted at ${money(l.amount)}, above its usual ${money(l.ceiling)} ceiling — worth a line on why.`,
      })
    }
  }
  return out
}

/* ── Versions ────────────────────────────────────────────────────────────── */

/** The list as it stands, in the shape `price_list_versions.snapshot` stores. */
export function snapshot({ programs = [], services = [], workPackages = [], policies = [] } = {}) {
  const grouped = byProgram(programs, services, workPackages)
  return {
    programs: grouped.map(({ services: _s, ...p }) => p),
    services: grouped.flatMap(p => p.services.map(s => ({ ...s, work_packages: s.work_packages.map(w => w.name) }))),
    policies: [...(policies || [])].sort((a, b) => n(a.sort_order) - n(b.sort_order)),
  }
}

export function nextVersion(versions = []) {
  return (versions || []).reduce((m, v) => Math.max(m, n(v.version)), 0) + 1
}

/* ── Loading the list ────────────────────────────────────────────────────── */

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : [])

/** A service row that is really a service: an id, a name, and not retired. */
const usable = (s) => Boolean(s && s.id && typeof s.service === 'string' && s.service.trim() && s.offering !== 'archived')

/**
 * The price list, as quoted from.
 *
 * The latest *published* version is preferred — it is the list somebody
 * reviewed — and its snapshot is read as stored. With nothing published yet
 * the live tables are the list, exactly as the studio reads them. Either way
 * the caller gets the same shape, plus where it came from.
 *
 * Never throws. A failed or empty read comes back with `error` set and no
 * services, so a screen can fall back to what it had before the list existed
 * rather than showing an empty picker.
 */
export async function loadPriceList(client) {
  const empty = { programs: [], services: [], workPackages: [], policies: [], version: null, source: null }
  if (!client?.from) return { ...empty, error: 'No database client.' }
  try {
    const pub = await client.from('price_list_versions')
      .select('version, status, snapshot')
      .eq('status', 'published')
      .order('version', { ascending: false })
      .limit(1)
    const latest = rowsOf(pub)[0]
    const snap = latest?.snapshot
    if (snap && Array.isArray(snap.services) && snap.services.some(usable)) {
      const services = snap.services.filter(usable)
      return {
        programs: Array.isArray(snap.programs) ? snap.programs : [],
        services,
        // The snapshot keeps work packages as names on each service.
        workPackages: services.flatMap(s => (Array.isArray(s.work_packages) ? s.work_packages : []).map((w, i) => ({
          service_id: s.id, name: typeof w === 'string' ? w : w?.name, sort_order: i,
        }))),
        policies: Array.isArray(snap.policies) ? snap.policies : [],
        version: n(latest.version) || null,
        source: 'published',
        error: null,
      }
    }

    const [p, sv, w, pol, v] = await Promise.all([
      client.from('catalog_programs').select('*').order('sort_order'),
      client.from('catalog_services').select('*').neq('offering', 'archived'),
      client.from('catalog_work_packages').select('*').order('sort_order'),
      client.from('catalog_policies').select('*').order('sort_order'),
      client.from('price_list_versions').select('version').order('version', { ascending: false }).limit(1),
    ])
    const failed = [p, sv].find(r => r?.error)
    if (failed) return { ...empty, error: failed.error.message || 'Could not read the price list.' }
    const services = rowsOf(sv).filter(usable)
    if (!services.length) return { ...empty, error: 'The price list is empty.' }
    return {
      programs: rowsOf(p),
      services,
      workPackages: rowsOf(w),
      policies: rowsOf(pol),
      version: n(rowsOf(v)[0]?.version) || null,
      source: 'live',
      error: null,
    }
  } catch (e) {
    return { ...empty, error: e?.message || 'Could not read the price list.' }
  }
}

/* ── Invoice presets ─────────────────────────────────────────────────────── */

export const PROGRAM_ICONS = {
  'brand-strategy': '🎯',
  'brand-identity': '🎨',
  'website-growth': '🌐',
  'marketing-leadership': '🔄',
  'add-ons': '🧩',
}

/**
 * The invoice form's line library, from the price list.
 *
 * One tab per program, one preset per service at its starting price — the
 * same figure a proposal opens at — so an invoice raised by hand cannot quote
 * a number the list retired. Retainers are their monthly fee.
 *
 * Returns null when the list has nothing to offer; the caller keeps its old
 * presets in that case rather than showing an empty library.
 */
export function invoicePresets(list = {}) {
  const groups = byProgram(list.programs, list.services, list.workPackages)
  if (!groups.length) return null
  const out = {}
  for (const g of groups) {
    out[g.name] = g.services.map(s => ({
      description: s.service,
      details: s.summary || '',
      rate: n(s.price_amount),
      icon: PROGRAM_ICONS[g.id] || '📋',
      service_id: s.id,
      recurring: isRetainer(s),
    }))
  }
  return out
}

/**
 * The library the invoice form shows: the price list when it loaded, the
 * presets it had before the list existed when it did not. `source` says which,
 * so the form can say so.
 */
export function presetLibrary(list, fallback = {}) {
  const presets = list && !list.error ? invoicePresets(list) : null
  if (presets) return { presets, categories: Object.keys(presets), source: list.source || 'live', version: list.version ?? null }
  return { presets: fallback, categories: Object.keys(fallback || {}), source: 'fallback', version: null }
}
